import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { SurveyTemplate, SurveyRespondent } from '@/types/database';
import { Loader2, Mail, MessageSquare, AlertCircle } from 'lucide-react';

interface SendSurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: SurveyTemplate;
  respondents: SurveyRespondent[];
  onSend: (respondentIds: string[], method: 'email' | 'sms') => Promise<void>;
}

export function SendSurveyModal({
  isOpen,
  onClose,
  template,
  respondents,
  onSend
}: SendSurveyModalProps) {
  const [selectedRespondents, setSelectedRespondents] = useState<string[]>([]);
  const [sendMethod, setSendMethod] = useState<'email' | 'sms'>('email');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedRespondents.length === 0) return;

    setSending(true);
    setError(null);

    try {
      await onSend(selectedRespondents, sendMethod);
      onClose();
    } catch (err) {
      console.error('Error sending survey:', err);
      setError(err instanceof Error ? err.message : 'Failed to send survey');
    } finally {
      setSending(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedRespondents.length === respondents.length) {
      setSelectedRespondents([]);
    } else {
      setSelectedRespondents(respondents.map(r => r.id));
    }
  };

  const toggleRespondent = (id: string) => {
    if (selectedRespondents.includes(id)) {
      setSelectedRespondents(selectedRespondents.filter(r => r !== id));
    } else {
      setSelectedRespondents([...selectedRespondents, id]);
    }
  };

  // Filter respondents based on send method
  const validRespondents = respondents.filter(r => 
    sendMethod === 'email' ? !!r.email : !!r.phone_number
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
          Send Survey: {template.title}
        </h2>

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded flex items-center">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Send Method Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Send Method
            </label>
            <div className="flex space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="email"
                  checked={sendMethod === 'email'}
                  onChange={(e) => setSendMethod(e.target.value as 'email' | 'sms')}
                  className="form-radio text-blue-600"
                />
                <span className="ml-2 text-gray-700 dark:text-gray-300">Email</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="sms"
                  checked={sendMethod === 'sms'}
                  onChange={(e) => setSendMethod(e.target.value as 'email' | 'sms')}
                  className="form-radio text-blue-600"
                />
                <span className="ml-2 text-gray-700 dark:text-gray-300">SMS</span>
              </label>
            </div>
          </div>

          {/* Respondent Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Select Recipients ({validRespondents.length} available)
              </label>
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                {selectedRespondents.length === validRespondents.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-64 overflow-y-auto">
              {validRespondents.map((respondent) => (
                <label
                  key={respondent.id}
                  className="flex items-center px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedRespondents.includes(respondent.id)}
                    onChange={() => toggleRespondent(respondent.id)}
                    className="form-checkbox text-blue-600"
                  />
                  <span className="ml-3 text-gray-900 dark:text-white">
                    {respondent.first_name} {respondent.last_name}
                  </span>
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                    ({sendMethod === 'email' ? respondent.email : respondent.phone_number})
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={selectedRespondents.length === 0 || sending}
            >
              {sending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  {sendMethod === 'email' ? (
                    <Mail className="h-4 w-4 mr-2" />
                  ) : (
                    <MessageSquare className="h-4 w-4 mr-2" />
                  )}
                  Send Survey ({selectedRespondents.length} selected)
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}